import { useEffect, type RefObject } from "react";

/**
 * Keeps the keyboard-focused item visible inside a scroll container.
 *
 * Scrolls the active row (the one marked `data-active`) into view whenever
 * `activeIndex` changes. `data-active` is set straight from `selectedIndex` in
 * the same render, so it is always current when this effect runs — keying off
 * the list's own selected-state instead would lag the scroll one row behind,
 * since that updates on a later render cycle. Uses `block: "nearest"` so it
 * only scrolls when the item is off-screen — no jumpiness or overshoot at the
 * list edges, and a no-op when the item is already visible. Instant (no smooth
 * behavior) to avoid lag while an arrow key is held down.
 *
 * Shared by the chat-input listings (slash command menu, mention dropdown,
 * file mention dropdown) so keyboard navigation auto-scrolls consistently.
 */
export function useScrollActiveIntoView(
  containerRef: RefObject<HTMLElement | null>,
  activeIndex: number,
  enabled: boolean,
) {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const active = containerRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    active?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, enabled, containerRef]);
}
